import React, { useState } from "react";
import Button from "src/components/Inputs/Button";
import { yearRange } from "src/utils";
import {
  CalculatorSettings,
  StoredCalculator,
  calculateProjection,
  getReturns,
} from "./Versatile/versatileTypes";
import {
  ArrowDownIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TableCellsIcon,
} from "@heroicons/react/24/outline";
import { useInfo } from "src/hooks/useData";
import Layout from "src/components/Layout";
import { Tooltip } from "flowbite-react";
import VersatileBalance from "src/components//Charts/VersatileBalance";
import Solve from "./Versatile/Solve";
import Table from "./Versatile/Table";
import ChartModal from "src/components/ChartModal";
import { useFullscreen } from "src/hooks/useFullScreen";
import Input from "../Inputs/Input";
import SmallToggle from "../Inputs/SmallToggle";
import Modal from "../Modal";
import Scenarios from "./Versatile/Scenarios";
import Select from "../Inputs/Select";

export const initialAnnuityRoR = {
  user: {
    startAge: 0,
    endValue: 0,
    presentValue: 0,
    endYear: 10,
  },
  payment: {
    amount: null as any,
    timing: "beginning",
    increase: null as any,
    startYear: null as any,
    detailedIncrease: 0,
    years: {},
    type: "simple",
  },
  other: {
    taxRate: 0,
    inflation: 0,
    investmentFee: 0,
  },
  solve: {
    field: "return",
  },
  returns: {
    rateOfReturn: null as any,
    returnType: "simple",
    mean: null as any,
    std: null as any,
    selectedRandom: "mean",
    yearlyReturns: {},
    seed: 0,
  },
};

const AnnuityRoR: React.FC = () => {
  const { data, setField } = useInfo();
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState(data.annuityRoRCalculators[0].id);
  const print = false;

  const settings: StoredCalculator = data.annuityRoRCalculators.find(
    (i) => i.id === tab,
  ) as any;
  const [openYears, setOpenYears] = useState(false);
  const [openReturns, setOpenReturns] = useState(false);

  const updateSettings = (
    category: keyof CalculatorSettings,
    field: string,
    value: number | string,
  ) => {
    setField("annuityRoRCalculators")(
      data.annuityRoRCalculators.map((item) =>
        item.id === tab
          ? {
              ...settings,
              [category]: {
                ...settings[category],
                [field]: value,
              },
            }
          : item,
      ),
    );
  };

  const returnsMemo = getReturns(settings);
  const calculations = calculateProjection(settings, returnsMemo);
  console.log("Calc", calculations, settings);

  const chartData = [
    {
      label: "Balance",
      data: calculations,
      returns: yearRange(1, settings.user.endYear).map((i) => returnsMemo(i)),
      color: "#3498db", // Indigo color
    },
  ];
  const { isFullscreen } = useFullscreen();

  return (
    <Layout page="calculator" wide>
      <div className="max-w-[1600px] mx-auto px-4 mt-[-85px]">
        <Scenarios
          tab={tab}
          setTab={setTab}
          calculators={data.annuityRoRCalculators}
          setCalculators={setField("annuityRoRCalculators")}
          calculatorType="annuity-ror"
        />
        <div className="flex gap-6">
          <div>
            <div className="sticky " style={{ top: isFullscreen ? 107 : 169 }}>
              <div
                className={`grid ${print ? "grid-cols-2 grid-rows-2" : "grid-cols-1"} gap-6`}
              >
                <div className="flex flex-col gap-4 border p-4 rounded-lg shadow-md print:shadow-none bg-white">
                  <div className="col-span-3">
                    <h2 className="text-xl font-semibold">User Settings</h2>
                  </div>
                  <div className="flex flex-col gap-3 justify-between">
                    <div className="flex flex-col gap-3 min-w-[400px]">
                      <Input
                        labelLength={100}
                        width="w-[130px]!"
                        label="Initial Premium"
                        subtype="money"
                        value={
                          settings.user.presentValue !== null
                            ? Math.round(settings.user.presentValue)
                            : null
                        }
                        setValue={(value) =>
                          updateSettings("user", "presentValue", value)
                        }
                      />{" "}
                    </div>
                    <div className="flex flex-col gap-3">
                      <Input
                        label="Total Years"
                        subtype="number"
                        labelLength={100}
                        width="w-[50px]!"
                        value={settings.user.endYear}
                        setValue={(value) =>
                          updateSettings("user", "endYear", value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-4 border p-4 rounded-lg shadow-md print:shadow-none bg-white">
                  <div className="flex gap-7 justify-between items-center">
                    <h2 className="text-xl font-semibold">Income</h2>
                    <div className="flex">
                      <SmallToggle
                        item1="Simple"
                        item2="Schedule"
                        active={
                          settings.payment.type === "simple"
                            ? "Simple"
                            : "Schedule"
                        }
                        toggle={() =>
                          updateSettings(
                            "payment",
                            "type",
                            settings.payment.type === "simple"
                              ? "schedule"
                              : "simple",
                          )
                        }
                      />
                    </div>
                  </div>
                  {settings.payment.type === "schedule" && (
                    <div className="w-32 mx-auto print:hidden">
                      <Button type="primary" onClick={() => setOpenYears(true)}>
                        Open Years{" "}
                      </Button>
                    </div>
                  )}
                  {settings.payment.type === "simple" && (
                    <div className="flex gap-3 flex-col">
                      <div className="flex gap-2 w-[241px]">
                        <Input
                          labelLength={130}
                          label="Payment ($)"
                          subtype="money"
                          size="md"
                          width="w-[106px]!"
                          value={
                            settings.payment.amount !== null
                              ? Math.round(settings.payment.amount)
                              : null
                          }
                          setValue={(value) =>
                            updateSettings("payment", "amount", value)
                          }
                        />
                        <div className="print:hidden">
                          <Tooltip
                            content="Copy payment to every year in Schedule."
                            theme={{ target: "" }}
                          >
                            <Button
                              type="secondary"
                              onClick={() => {
                                updateSettings(
                                  "payment",
                                  "years",
                                  Object.fromEntries(
                                    yearRange(
                                      settings.payment.startYear,
                                      settings.user.endYear,
                                    ).map((i) => [i, settings.payment.amount]),
                                  ) as any,
                                );
                              }}
                            >
                              <TableCellsIcon className="h-5 w-5" />
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                      <Input
                        label="Years of Deferral"
                        subtype="number"
                        labelLength={130}
                        width="w-[80px]!"
                        value={settings.payment.startYear}
                        setValue={(value) =>
                          updateSettings("payment", "startYear", value)
                        }
                      />
                    </div>
                  )}
                  <div className="flex gap-3 flex-col">
                    <Input
                      label="Increase (%)"
                      labelLength={130}
                      width="w-[80px]!"
                      subtype="percent"
                      value={settings.payment.increase}
                      setValue={(value) =>
                        updateSettings("payment", "increase", value)
                      }
                    />
                    <div className="w-[212px]">
                      <Select
                        labelLength={130}
                        label="Timing"
                        width="w-[80px]!"
                        options={[
                          { id: "beginning", name: "BoY" },
                          { id: "end", name: "EoY" },
                        ]}
                        selected={{
                          id: settings.payment.timing,
                          name:
                            settings.payment.timing === "beginning"
                              ? "BoY"
                              : "EoY",
                        }}
                        setSelected={(option) =>
                          updateSettings("payment", "timing", option.id)
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-4 border p-4 rounded-lg shadow-md print:shadow-none bg-white">
                  <div className="flex justify-between items-center w-full">
                    <h2 className="text-xl font-semibold">Return</h2>
                    <div className="flex flex-col gap-6 ">
                      <div className="flex gap-2 w-[180px]">
                        <Input
                          label="Return (%)"
                          labelLength={80}
                          subtype="percent"
                          value={
                            Math.round(settings.returns.rateOfReturn * 100) /
                            100
                          }
                          setValue={(value) =>
                            updateSettings("returns", "rateOfReturn", value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#f3f4f6] flex flex-col justify-between items-center gap-5">
                  <div className="flex gap-4 items-center flex-col">
                    <Solve
                      settings={settings as any}
                      simple
                      updateCalculator={(settings: any) =>
                        setField("annuityRoRCalculators")(
                          data.annuityRoRCalculators.map((item) =>
                            item.id === tab ? settings : item,
                          ),
                        )
                      }
                    />
                    <div className="w-40">
                      <Button
                        type="secondary"
                        onClick={() => {
                          setField("annuityRoRCalculators")(
                            data.annuityRoRCalculators.map((item) =>
                              item.id === tab
                                ? {
                                    ...item,
                                    ...initialAnnuityRoR,
                                  }
                                : item,
                            ),
                          );
                        }}
                      >
                        Reset Inputs
                      </Button>
                    </div>
                  </div>
                </div>

                <Modal isOpen={openYears} onClose={() => setOpenYears(false)}>
                  <div>
                    <div className="mb-3 font-semibold text-lg">
                      Manual Input
                    </div>
                    <div className="mb-5 w-64 mx-auto">
                      <Input
                        label="Increase (%)"
                        subtype="percent"
                        value={settings.payment.detailedIncrease}
                        setValue={(value) =>
                          updateSettings("payment", "detailedIncrease", value)
                        }
                      />
                    </div>
                    <div className="flex flex-col max-h-[500px] overflow-scroll gap-2 mb-4">
                      {yearRange(
                        settings.payment.startYear,
                        settings.user.endYear,
                      ).map((i) => (
                        <div className="flex gap-3">
                          <Input
                            label={`Year ${i}`}
                            subtype="money"
                            onPaste={(_: any) => {
                              // e.preventDefault();
                              // const text = e.clipboardData.getData("text");
                              // const values = text.split("\n") as any[];
                              // const newValues = values
                              //   .filter((i) => i)
                              //   .map((v) => parseFloat(v))
                              //   .filter((i) => !isNaN(i));
                              // console.log(text, values, newValues);
                              // if (newValues.length === 1) {
                              //   updateSettings("payment", "years", {
                              //     ...settings.payment.years,
                              //     [i + settings.payment.startYear]: newValues[0],
                              //   } as any);
                              // } else {
                              //   updateSettings("payment", "years", {
                              //     ...settings.payment.years,
                              //     ...(Object.fromEntries(
                              //       newValues.map((v, j) => [j + i, v]),
                              //     ) as any),
                              //   });
                              // }
                            }}
                            value={settings.payment.years[i]}
                            setValue={(value) =>
                              updateSettings("payment", "years", {
                                ...settings.payment.years,
                                [i]: value,
                              } as any)
                            }
                          />
                          <div className="w-12">
                            <Button
                              type="secondary"
                              onClick={() => {
                                updateSettings(
                                  "payment",
                                  "years",
                                  Object.fromEntries(
                                    yearRange(
                                      settings.payment.startYear,
                                      settings.user.endYear,
                                    ).map((k) => {
                                      if (k > i)
                                        return [
                                          k,
                                          Math.round(
                                            settings.payment.years[i] *
                                              Math.pow(
                                                1 +
                                                  (settings.payment
                                                    .detailedIncrease || 0) /
                                                    100,
                                                k - i,
                                              ),
                                          ),
                                        ];
                                      else
                                        return [k, settings.payment.years[k]];
                                    }),
                                  ) as any,
                                );
                              }}
                            >
                              <div className="flex items-center justify-center h-6">
                                <ArrowDownIcon className="w-4 h-4" />
                              </div>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Modal>
                <Modal
                  isOpen={openReturns}
                  onClose={() => setOpenReturns(false)}
                >
                  {settings.returns.returnType === "schedule" && (
                    <div>
                      <div className="mb-3 font-semibold text-lg">
                        Return Rates by Year
                      </div>
                      <div className="flex flex-col max-h-[500px] overflow-scroll gap-2 mb-4">
                        {yearRange(1, settings.user.endYear).map((year) => (
                          <div className="flex gap-3" key={year}>
                            <Input
                              label={`Year ${year}`}
                              subtype="percent"
                              value={settings.returns.yearlyReturns[year]}
                              setValue={(value) =>
                                updateSettings("returns", "yearlyReturns", {
                                  ...settings.returns.yearlyReturns,
                                  [year]: value,
                                } as any)
                              }
                            />
                            <div className="w-12">
                              <Button
                                type="secondary"
                                onClick={() => {
                                  updateSettings(
                                    "returns",
                                    "yearlyReturns",
                                    Object.fromEntries(
                                      [
                                        ...Array(
                                          settings.user.endYear + 1,
                                        ).keys(),
                                      ].map((k) => {
                                        if (k > year)
                                          return [
                                            k,
                                            settings.returns.yearlyReturns[
                                              year
                                            ],
                                          ];
                                        return [
                                          k,
                                          settings.returns.yearlyReturns[k],
                                        ];
                                      }),
                                    ) as any,
                                  );
                                }}
                              >
                                <div className="flex items-center justify-center h-6">
                                  <ArrowDownIcon className="w-4 h-4" />
                                </div>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </Modal>
              </div>
            </div>
          </div>
          <div className="w-[1200px]">
            <div
              className="bg-[#f3f4f6] sticky top-[122px] pt-12 mt-[-100px]"
              style={{ top: isFullscreen ? 107 : 122 }}
            >
              <div className="bg-white py-3 pr-3 border rounded-lg shadow-md ">
                {
                  <div className={`flex justify-end cursor-pointer `}>
                    {open ? (
                      <ChevronUpIcon
                        className="text-[#475467] w-6"
                        onClick={() => setOpen(!open)}
                      />
                    ) : (
                      <ChevronDownIcon
                        className="text-[#475467] w-6"
                        onClick={() => setOpen(!open)}
                      />
                    )}
                    <ChartModal
                      onClose={() =>
                        document
                          .querySelectorAll(`.tooltip-modal`)
                          .forEach((el) => {
                            el.remove();
                          })
                      }
                    >
                      <VersatileBalance
                        datasets={chartData}
                        print={false}
                        full
                        prefix="modal"
                      />
                    </ChartModal>
                  </div>
                }

                <div
                  className={` transition-maxHeight w-full duration-500 ease-in-out ${open ? "max-h-[1500px]" : "max-h-0 overflow-hidden"}`}
                >
                  <VersatileBalance
                    datasets={chartData}
                    print={false}
                    prefix="main"
                  />
                </div>
              </div>
            </div>
            <div className="mt-[150px]">
              <Table
                simple
                calculations={calculations}
                returnsMemo={returnsMemo}
                open={open}
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AnnuityRoR;
